// ============================================================================
// Overall Control — Shipments tab on real data (spec: docs/overall-control/SHIPMENTS_TAB_SPEC.md,
// answers SH1–SH10). Service POs are excluded throughout.
//
// Summary (two rows): Total shipments / Shipment not created · Delivered / Not delivered ·
// At risk / Breached · Next day / Open escalations. Stuck by stage at PO level. Delay ownership by
// First mile · Warehouse · Last mile · Escalations. Breakdown: First mile by receiving dock, Last
// mile by pickup warehouse or Control Tower lane. Team by shipment owner, last or first mile.
// ============================================================================
import { Alert, Segmented, Skeleton, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { getShipmentsOverview } from "../../api/orders";
import { fmtWeight, STATUS_META } from "../../format";
import {
  OcFirstMileBreakdownRow,
  OcLastMileBreakdownRow,
  OcRow,
  OcScopeParams,
  OcShipmentOwnerRow,
  OcWarehouseOwnerRow,
} from "../../types/orders";
import MetricLink from "../metric_link";
import OrdersListDrawer, { OrdersListRequest } from "../orders/orders_list_drawer";
import {
  AtRiskCell,
  BreachedCell,
  Cell,
  DASH,
  EscalationTeamsTable,
  OpenEscalationsCell,
  PctText,
  Section,
  StagesTable,
  TeamTable,
  TechErrorsCell,
  useTabOverview,
  whenLabel,
} from "../real/common";
import styles from "../../index.module.scss";

interface Props {
  scope: OcScopeParams;
  rangeLabel: string;
  onStatus: (s: { loading: boolean; updatedAt: Date | null; refresh: () => void }) => void;
  onOpenPath: (r: OcRow) => void;
}

// Delay-ownership groups (SH8): Transfer sits with First mile, as in the Control Tower.
const OWNERSHIP_ROWS = [
  { team: "First mile", stages: "First-mile pickup, Transfer" },
  { team: "Warehouse", stages: "GRN, Picklist" },
  { team: "Last mile", stages: "Shipment, Re-attempt delivery" },
];

type BreakdownView = "first_mile" | "warehouse" | "lane";
type Mile = "last_mile" | "first_mile" | "warehouse";

const ShipmentsTab: React.FC<Props> = ({ scope, rangeLabel, onStatus, onOpenPath }) => {
  const { data, error, loading, updatedAt, refresh } = useTabOverview(getShipmentsOverview, scope, "Shipments");
  const [drawer, setDrawer] = useState<OrdersListRequest | null>(null);
  const [view, setView] = useState<BreakdownView>("warehouse");
  const [mile, setMile] = useState<Mile>("last_mile");

  useEffect(() => onStatus({ loading, updatedAt, refresh }), [loading, updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (title: string, metric: string) => setDrawer({ mode: "metric", title, metric, scope, tab: "shipments" });
  const drill = (title: string, metric: string) => () => open(title, metric);
  const when = whenLabel(rangeLabel);

  if (error && !data) return <Alert type="error" showIcon message={error} className={styles.inlineAlert} />;
  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const s = data.summary;
  const next = dayjs(s.next_day.date);
  const nextLabel = next.isSame(dayjs().add(1, "day"), "day") ? "tomorrow" : `· ${next.format("D MMM")}`;

  return (
    <div>
      {error ? <Alert type="warning" showIcon message={`Showing the last loaded numbers: ${error}`} className={styles.inlineAlert} /> : null}

      <div className={styles.panel}>
        <div className={styles.summaryGrid5}>
          {/* Row 1 */}
          <Cell
            tone="brand"
            label={`Total shipments ${when}`}
            sub={
              <>
                {fmtWeight(s.total_shipments.weight_kg)} ·{" "}
                <MetricLink
                  value={`${s.total_shipments.reattempts} re-attempts`}
                  count={s.total_shipments.reattempts}
                  onClick={drill("Re-attempt shipments", "reattempt_shipments")}
                />
              </>
            }
          >
            <Tooltip title="Last-mile shipments due in the range. Return pickups are not included yet.">
              <span>
                <MetricLink value={s.total_shipments.count} count={s.total_shipments.count} onClick={drill(`Shipments ${when}`, "total_shipments")} />
              </span>
            </Tooltip>
          </Cell>
          <Cell
            tone="good"
            label="Delivered"
            sub={
              <>
                {s.delivered.pct == null ? "–" : `${s.delivered.pct}%`} ·{" "}
                <Tooltip title="Due on an earlier day, delivered in this range. Not part of Delivered %.">
                  <span>
                    <MetricLink
                      value={`+${s.delivered.earlier_dated} earlier-dated`}
                      count={s.delivered.earlier_dated}
                      onClick={drill("Earlier-dated shipments delivered in this range", "earlier_delivered")}
                    />
                  </span>
                </Tooltip>
              </>
            }
          >
            <MetricLink
              value={s.delivered.count}
              count={s.delivered.count}
              color={STATUS_META.done_on_time.text}
              onClick={drill(`Delivered ${when}`, "delivered")}
            />
          </Cell>
          <AtRiskCell v={s.at_risk} onDrill={open} label="Delivery at risk" />
          <Cell
            tone="info"
            label={`Next day ${nextLabel}`}
            sub={
              <Tooltip title="POs due that day with no shipment yet. Not part of the shipments count.">
                <span>
                  <MetricLink
                    value={`+${s.next_day.not_created} not created`}
                    count={s.next_day.not_created}
                    onClick={drill(`Next day ${nextLabel} · shipment not created`, "next_day_not_created")}
                  />
                </span>
              </Tooltip>
            }
          >
            <MetricLink value={s.next_day.shipments} count={s.next_day.shipments} onClick={drill(`Next day ${nextLabel}`, "next_day")} />
          </Cell>
          <OpenEscalationsCell esc={s.escalations} onDrill={open} />

          {/* Row 2 */}
          <Cell
            tone="warn"
            label="Shipment not created"
            sub={
              <>
                <MetricLink
                  value={`+${s.not_created.older} older`}
                  count={s.not_created.older}
                  onClick={drill("Shipment not created · older", "not_created_older")}
                />
                {" · "}
                <Tooltip title="Shown under Awaiting Payment on the OMS Operations board, not under Missing.">
                  <span>
                    <MetricLink
                      value={`${s.not_created.awaiting_payment} awaiting payment`}
                      count={s.not_created.awaiting_payment}
                      onClick={drill("Awaiting payment", "awaiting_payment")}
                    />
                  </span>
                </Tooltip>
              </>
            }
          >
            <Tooltip title="The OMS Operations Last Mile Missing orders list for the range.">
              <span>
                <MetricLink value={s.not_created.count} count={s.not_created.count} onClick={drill(`Shipment not created ${when}`, "not_created")} />
              </span>
            </Tooltip>
          </Cell>
          <Cell
            tone="violet"
            label="Not delivered"
            sub={
              <>
                {s.not_delivered.pct == null ? "–" : `${s.not_delivered.pct}%`} ·{" "}
                <Tooltip title="POs due in the range with no shipment yet (the Shipment not created block). Not part of the shipments count.">
                  <span>
                    <MetricLink
                      value={`+${s.not_delivered.not_created} not created`}
                      count={s.not_delivered.not_created}
                      onClick={drill(`Shipment not created ${when}`, "not_created")}
                    />
                  </span>
                </Tooltip>
              </>
            }
          >
            <MetricLink
              value={s.not_delivered.count}
              count={s.not_delivered.count}
              color={STATUS_META.undelivered.text}
              onClick={drill(`Not delivered ${when}`, "not_delivered")}
            />
          </Cell>
          <BreachedCell v={s.breached} onDrill={open} label="Delivery breached" />
          {/* Awaiting payment has no block of its own: it is a link under Shipment not created. */}
          <Cell
            tone="good"
            label="OTIF"
            sub={
              <MetricLink
                value={`${s.otif.breached} breached of ${s.otif.total} shipments`}
                count={s.otif.breached}
                onClick={drill(`OTIF · breached shipments ${when}`, "otif_breached")}
              />
            }
          >
            <Tooltip
              title={`(Total shipments − breached shipments) / Total shipments = (${s.otif.total} − ${s.otif.breached}) / ${s.otif.total}. Breaches excused by an escalation are not counted.`}
            >
              <span>{s.otif.pct == null ? "–" : `${s.otif.pct}%`}</span>
            </Tooltip>
          </Cell>
          <TechErrorsCell tech={s.tech_errors} onDrill={open} />
        </div>
      </div>

      <Section title="Stuck by stage">
        <div className={styles.sectionNote}>Per PO. A PO with a re-attempt is counted under Re-attempt only.</div>
        <StagesTable stages={data.stages} onDrill={open} compact />
      </Section>

      <Section title="Delay ownership">
        <div className={styles.sectionNote}>Delayed, TAT and the rest arrive with the at-risk and breach rules.</div>
        <TeamTable teams={OWNERSHIP_ROWS} first="Owner" columns={["Delayed", "TAT", "Weight", "Avg slip"]} />
        <h4 className={styles.subhead}>Escalations · by the team that resolves them</h4>
        <EscalationTeamsTable rows={data.escalation_teams} onDrill={open} />
      </Section>

      <Section title="Breakdown">
        <div className={styles.sectionToolbar}>
          <Segmented
            size="small"
            value={view}
            onChange={(v) => setView(v as BreakdownView)}
            options={[
              { label: "Last mile · pickup warehouse", value: "warehouse" },
              { label: "Last mile · lane", value: "lane" },
              { label: "First mile · receiving dock", value: "first_mile" },
            ]}
          />
        </div>
        {view === "first_mile" ? (
          <FirstMileTable rows={data.breakdown.first_mile} onDrill={open} />
        ) : (
          <LastMileTable view={view} rows={data.breakdown[view]} total={data.breakdown.last_mile_total} onDrill={open} />
        )}
      </Section>

      <Section title="Team · by owner">
        <div className={styles.sectionToolbar}>
          <Segmented
            size="small"
            value={mile}
            onChange={(v) => setMile(v as Mile)}
            options={[
              { label: "Last mile", value: "last_mile" },
              { label: "First mile", value: "first_mile" },
              { label: "Warehouse · picklist / GRN", value: "warehouse" },
            ]}
          />
          <span className={styles.muted}>
            {mile === "warehouse"
              ? "Owner = OMS's owner on the leg when it has one, else the warehouse desk sheet (express picked the same day it is delivered vs leftovers)."
              : "Owner = the person assigned to the truck in OMS."}
          </span>
        </div>
        {mile === "warehouse" ? (
          <WarehouseOwnerTable rows={data.teams.warehouse} onDrill={open} />
        ) : (
          <OwnerTable mile={mile} rows={data.teams[mile]} onDrill={open} />
        )}
      </Section>

      <OrdersListDrawer request={drawer} onClose={() => setDrawer(null)} onOpenPath={onOpenPath} />
    </div>
  );
};

const pending = { align: "right" as const, width: 90, render: () => DASH };

const FirstMileTable: React.FC<{ rows: OcFirstMileBreakdownRow[]; onDrill: (t: string, m: string) => void }> = ({ rows, onDrill }) => {
  const m = (part: string, label: string) => (v: number, r: OcFirstMileBreakdownRow) =>
    <MetricLink value={v} count={v} onClick={() => onDrill(`${r.label} · ${label}`, `bd:first_mile:${r.key}:${part}`)} />;
  const columns: ColumnsType<OcFirstMileBreakdownRow> = [
    { title: "Receiving dock", dataIndex: "label", render: (v: string) => <span className={styles.strong}>{v}</span> },
    { title: "POs", dataIndex: "total", align: "right", width: 90, render: m("pos", "POs") },
    { title: "Picked up / delivered", dataIndex: "picked", align: "right", width: 150, render: m("picked", "first mile done") },
    { title: "GRN done", dataIndex: "grn_done", align: "right", width: 100, render: m("grn_done", "GRN done") },
    { title: "Waiting at GRN", dataIndex: "grn_waiting", align: "right", width: 120, render: m("grn_waiting", "waiting at GRN") },
    { title: "At risk", key: "at_risk", ...pending },
    { title: "Breached", key: "breached", ...pending },
  ];
  return (
    <Table<OcFirstMileBreakdownRow>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 900 }}
      locale={{ emptyText: "No first-mile legs in this range." }}
    />
  );
};

const LastMileTable: React.FC<{
  view: "warehouse" | "lane";
  rows: OcLastMileBreakdownRow[];
  total: Omit<OcLastMileBreakdownRow, "key" | "label">;
  onDrill: (t: string, m: string) => void;
}> = ({ view, rows, total, onDrill }) => {
  const m = (part: string, label: string, color?: string) => (v: number, r: OcLastMileBreakdownRow) =>
    <MetricLink value={v} count={v} color={color} onClick={() => onDrill(`${r.label} · ${label}`, `bd:${view}:${r.key}:${part}`)} />;
  const columns: ColumnsType<OcLastMileBreakdownRow> = [
    { title: view === "warehouse" ? "Pickup warehouse" : "Lane", dataIndex: "label", render: (v: string) => <span className={styles.strong}>{v}</span> },
    {
      title: <Tooltip title="POs on these shipments + POs not created yet">POs</Tooltip>,
      dataIndex: "pos",
      align: "right",
      width: 80,
      render: m("pos", "POs"),
    },
    { title: "Shipments", dataIndex: "total", align: "right", width: 100, render: m("trucks", "shipments") },
    { title: "Weight", dataIndex: "weight_kg", align: "right", width: 90, render: (v: number) => fmtWeight(v) },
    { title: "Delivered %", dataIndex: "delivered_pct", align: "right", width: 100, render: (v: number | null) => (v == null ? DASH : `${v}%`) },
    { title: "Delivered", dataIndex: "delivered", align: "right", width: 90, render: m("delivered", "delivered") },
    { title: "Not delivered", dataIndex: "not_delivered", align: "right", width: 110, render: m("not_delivered", "not delivered") },
    {
      title: "Not created",
      dataIndex: "not_created",
      align: "right",
      width: 100,
      render: (v: number | null, r) => (v == null ? DASH : m("not_created", "not created")(v, r)),
    },
    { title: "At risk", key: "at_risk", ...pending },
    {
      title: <Tooltip title="Shipments breached (SLA), as the Delivery breached block counts them">Breached</Tooltip>,
      dataIndex: "breached",
      align: "right",
      width: 90,
      render: m("breached", "breached", STATUS_META.breached.text),
    },
    {
      title: <Tooltip title="(Shipments − breached) / Shipments, as the OTIF block">OTIF %</Tooltip>,
      dataIndex: "otif_pct",
      align: "right",
      width: 90,
      render: (v: number | null) => <PctText value={v} />,
    },
  ];
  return (
    <Table<OcLastMileBreakdownRow>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 1000 }}
      locale={{ emptyText: "No shipments in this range." }}
      summary={() =>
        rows.length ? (
          <Table.Summary.Row className={styles.totalRow}>
            <Table.Summary.Cell index={0}>
              <Tooltip title="Each PO and shipment counted once. A PO split across shipments from two warehouses appears in both rows above.">
                <span className={styles.strong}>Total</span>
              </Tooltip>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <MetricLink value={total.pos} count={total.pos} onClick={() => onDrill("All POs · last mile", "bd:total:pos")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <MetricLink value={total.total} count={total.total} onClick={() => onDrill("All shipments", "total_shipments")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">{fmtWeight(total.weight_kg)}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">{total.delivered_pct == null ? DASH : `${total.delivered_pct}%`}</Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              <MetricLink value={total.delivered} count={total.delivered} onClick={() => onDrill("Delivered", "delivered")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              <MetricLink value={total.not_delivered} count={total.not_delivered} onClick={() => onDrill("Not delivered · shipments", "not_delivered")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right">
              <MetricLink value={total.not_created ?? 0} count={total.not_created ?? 0} onClick={() => onDrill("Shipment not created", "not_created")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="right">{DASH}</Table.Summary.Cell>
            <Table.Summary.Cell index={9} align="right">
              <MetricLink value={total.breached} count={total.breached} color={STATUS_META.breached.text} onClick={() => onDrill("Breached shipments", "otif_breached")} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={10} align="right">
              <PctText value={total.otif_pct} />
            </Table.Summary.Cell>
          </Table.Summary.Row>
        ) : null
      }
    />
  );
};

const WarehouseOwnerTable: React.FC<{ rows: OcWarehouseOwnerRow[]; onDrill: (t: string, m: string) => void }> = ({ rows, onDrill }) => {
  const m = (part: string, label: string, color?: string) => (v: number, r: OcWarehouseOwnerRow) =>
    <MetricLink value={v} count={v} color={color} onClick={() => onDrill(`${r.owner_name} · ${label}`, `team:warehouse:${r.owner_id}:${part}`)} />;
  const columns: ColumnsType<OcWarehouseOwnerRow> = [
    { title: "Owner", dataIndex: "owner_name", render: (v: string) => <span className={styles.strong}>{v}</span> },
    { title: "Picklists", dataIndex: "picklists", align: "right", width: 100, render: m("picklist", "picklists") },
    { title: "Picklists waiting", dataIndex: "picklists_waiting", align: "right", width: 140, render: m("picklist_waiting", "picklists waiting", STATUS_META.at_risk_minor.text) },
    { title: "Picked %", dataIndex: "picklists_done_pct", align: "right", width: 100, render: (v: number | null) => <PctText value={v} /> },
    { title: "GRNs", dataIndex: "grns", align: "right", width: 90, render: m("grn", "GRNs (incl. transfers in)") },
    { title: "GRNs waiting", dataIndex: "grns_waiting", align: "right", width: 120, render: m("grn_waiting", "GRNs waiting", STATUS_META.at_risk_minor.text) },
    { title: "Eligible GRNs", dataIndex: "grns_eligible", align: "right", width: 120, render: m("grn_eligible", "eligible GRNs (not heavy / large item)") },
    { title: "Marked % (eligible)", dataIndex: "grns_eligible_done_pct", align: "right", width: 150, render: (v: number | null) => <PctText value={v} /> },
    { title: "Picklists breached", dataIndex: "picklists_breached", align: "right", width: 140, render: m("picklist_breached", "picklists breached (SLA)", STATUS_META.breached.text) },
    { title: "GRNs breached", dataIndex: "grns_breached", align: "right", width: 120, render: m("grn_breached", "GRNs breached (SLA)", STATUS_META.breached.text) },
  ];
  return (
    <Table<OcWarehouseOwnerRow>
      size="small"
      rowKey="owner_id"
      columns={columns}
      dataSource={rows}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 800 }}
    />
  );
};

const OwnerTable: React.FC<{ mile: Mile; rows: OcShipmentOwnerRow[]; onDrill: (t: string, m: string) => void }> = ({ mile, rows, onDrill }) => {
  const m = (part: string, label: string, color?: string) => (v: number, r: OcShipmentOwnerRow) =>
    <MetricLink value={v} count={v} color={color} onClick={() => onDrill(`${r.owner_name} · ${label}`, `team:${mile}:${r.owner_id}:${part}`)} />;
  const columns: ColumnsType<OcShipmentOwnerRow> = [
    { title: "Shipment owner", dataIndex: "owner_name", render: (v: string) => <span className={styles.strong}>{v}</span> },
    { title: "Shipments", dataIndex: "shipments", align: "right", width: 100, render: m("trucks", "shipments") },
    { title: "Weight", dataIndex: "weight_kg", align: "right", width: 90, render: (v: number | null) => (v == null ? DASH : fmtWeight(v)) },
    { title: "Delivered %", dataIndex: "delivered_pct", align: "right", width: 100, render: (v: number | null) => (v == null ? DASH : `${v}%`) },
    { title: "Delivered", dataIndex: "delivered", align: "right", width: 90, render: m("delivered", "delivered") },
    { title: "Not delivered", dataIndex: "not_delivered", align: "right", width: 110, render: m("not_delivered", "not delivered") },
    ...(mile === "first_mile"
      ? [
          { title: "Eligible", dataIndex: "eligible", align: "right" as const, width: 90, render: m("eligible", "eligible (not heavy / large item)") },
          { title: "Delivered % (eligible)", dataIndex: "eligible_delivered_pct", align: "right" as const, width: 160, render: (v: number | null) => <PctText value={v} /> },
        ]
      : []),
    // Last mile shows OTIF % where First mile shows On time (user, 2026-10-17). They differ: On time counts
    // delivered shipments that crossed no SLA line; OTIF counts every shipment not breached.
    mile === "last_mile"
      ? {
          title: <Tooltip title="(Shipments − breached) / Shipments, as the OTIF block">OTIF %</Tooltip>,
          dataIndex: "otif_pct",
          align: "right" as const,
          width: 90,
          render: (v: number | null) => <PctText value={v} />,
        }
      : { title: "On time", dataIndex: "on_time", align: "right" as const, width: 90, render: m("on_time", "delivered on time (SLA)", STATUS_META.done_on_time.text) },
    { title: "Major", dataIndex: "major", align: "right", width: 80, render: m("major", "major (SLA)", STATUS_META.at_risk_major.text) },
    { title: "Minor", dataIndex: "minor", align: "right", width: 80, render: m("minor", "minor (SLA)", STATUS_META.at_risk_minor.text) },
    { title: "Breached", dataIndex: "breached", align: "right", width: 90, render: m("breached", "breached (SLA)", STATUS_META.breached.text) },
    {
      title: (
        <Tooltip title={`Escalations raised on the ${mile === "last_mile" ? "delivery (shipment / re-attempt)" : "pickup"} leg of this owner's shipments. One raised on a truck counts for that truck's owner.`}>
          Escalations
        </Tooltip>
      ),
      dataIndex: "escalations",
      align: "right",
      width: 110,
      render: m("escalations", "escalations raised"),
    },
    {
      title: "Open esc.",
      dataIndex: "open_escalations",
      align: "right",
      width: 100,
      render: (v: number | null, r) => (v == null ? DASH : m("esc_open", "open escalations", STATUS_META.at_risk_major.text)(v, r)),
    },
  ];
  return (
    <Table<OcShipmentOwnerRow>
      size="small"
      rowKey="owner_id"
      columns={columns}
      dataSource={rows}
      pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
      className={styles.denseTable}
      scroll={{ x: 1100 }}
      locale={{ emptyText: "No shipments in this range." }}
    />
  );
};

export default ShipmentsTab;
